import React, { type FC } from "react";
import classNames from "classnames";
import type { ApiMeasure } from "@src/api/dictionaries/measures";
import "./BusinessMeasureValue.scss";

/** Минимальный контракт единицы измерения для отображения. */
export type BusinessMeasureValueMeasure = Pick<ApiMeasure, "ShortName">;

/** Тон отображения единицы измерения. */
export type BusinessMeasureValueUnitTone = "muted" | "inherit";

/** Акцент единицы измерения. */
export type BusinessMeasureValueUnitEmphasis = "normal" | "italic";

/** Пропсы компонента {@link BusinessMeasureValue}. */
export interface BusinessMeasureValueProps {
    /** Значение количества или составное представление, например `3 / 10`. */
    value: React.ReactNode;
    /** Единица измерения; используется только `ShortName`. */
    measure?: BusinessMeasureValueMeasure | null;
    /**
     * Текст или узел, если `value` пустое (`null`, `undefined`, `""`). \
     * Если не задан, пустое значение не рендерится.
     */
    fallback?: React.ReactNode;
    /** Визуальный тон единицы измерения. @default "muted" */
    unitTone?: BusinessMeasureValueUnitTone;
    /** Акцент единицы измерения. @default "normal" */
    unitEmphasis?: BusinessMeasureValueUnitEmphasis;
    /** Дополнительный CSS-класс корневого контейнера. */
    className?: string;
}

function isEmptyValue(value: React.ReactNode): boolean {
    return value == null || value === "";
}

function resolveMeasureShortName(measure?: BusinessMeasureValueMeasure | null): string | null {
    const shortName = measure?.ShortName?.trim();
    return shortName === "" ? null : (shortName ?? null);
}

/**
 * Формирует plain-text представление значения с единицей измерения. \
 * Удобно для confirm-диалогов и других контекстов, где нужна строка.
 * @param value значение количества
 * @param measure единица измерения
 * @returns строка вида `10 кг` или только значение, если единица не задана
 */
export function formatMeasureValueText(
    value: string | number | null | undefined,
    measure?: BusinessMeasureValueMeasure | null,
): string {
    if (value == null || value === "") {
        return "";
    }

    const valueText = typeof value === "number" ? String(value) : value;
    const unit = resolveMeasureShortName(measure);

    if (unit == null) {
        return valueText;
    }

    return `${valueText} ${unit}`;
}

/**
 * Read-only отображение количества с единицей измерения. \
 * Значение остаётся основным текстом, `ShortName` выводится рядом вторичным тоном.
 *
 * @example
 * ```tsx
 * <BusinessMeasureValue value={content.DocQty} measure={content.Measure} />
 * <BusinessMeasureValue value={`${placedQty} / ${item.DepotQty}`} measure={item.Measure} />
 * ```
 */
export const BusinessMeasureValue: FC<BusinessMeasureValueProps> = ({
    value,
    measure,
    fallback,
    unitTone = "muted",
    unitEmphasis = "normal",
    className,
}) => {
    if (isEmptyValue(value)) {
        if (fallback != null) {
            return <span className={className}>{fallback}</span>;
        }

        return null;
    }

    const unit = resolveMeasureShortName(measure);

    return (
        <span className={classNames("business_measure_value", className)}>
            <span className="business_measure_value__value">{value}</span>
            {unit != null && (
                <span
                    className={classNames(
                        "business_measure_value__unit",
                        `business_measure_value__unit--${unitTone}`,
                        unitEmphasis === "italic" && "business_measure_value__unit--italic",
                    )}
                >
                    {unit}
                </span>
            )}
        </span>
    );
};
